import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../context/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [skillDistribution, setSkillDistribution] = useState([]);
  const [departmentBreakdown, setDepartmentBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, skillsResponse, deptResponse, profResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getSkillDistribution(),
        dashboardService.getDepartmentBreakdown(),
        dashboardService.getProficiencyDistribution()
      ]);
      
      // Handle wrapped responses
      const statsData = statsResponse.data || statsResponse;
      const skillsData = skillsResponse.data || skillsResponse;
      const deptData = deptResponse.data || deptResponse;
      const profData = profResponse.data || profResponse;
      
      // Ensure arrays
      const skills = Array.isArray(skillsData) ? skillsData : [];
      const departments = Array.isArray(deptData) ? deptData : [];
      
      // Calculate average skills per profile
      let avgSkills = 0;
      if (statsData.totalProfiles > 0 && skills.length > 0) {
        // Sum up all profile counts from skills
        const totalSkillAssignments = skills.reduce((sum, s) => sum + (s.profile_count || s.count || 0), 0);
        avgSkills = (totalSkillAssignments / statsData.totalProfiles).toFixed(1);
      }
      
      // Extract stats from response
      const stats = {
        total_profiles: statsData.totalProfiles || 0,
        total_skills: statsData.totalSkills || 0,
        total_departments: departments.length || 0,
        avg_skills_per_profile: avgSkills,
        proficiency_breakdown: profData || {
          beginner: 0,
          intermediate: 0,
          advanced: 0,
          expert: 0
        }
      };
      
      setStats(stats);
      setSkillDistribution(skills);
      setDepartmentBreakdown(departments);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  const isAdmin = user?.role === 'hr_admin' || user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Overview of talent and skills</p>
      </div>

      <div className="stats-grid">
        <StatCard
          title="Total Profiles"
          value={stats?.total_profiles || 0}
          icon="👥"
          onClick={() => navigate('/profiles')}
        />
        <StatCard
          title="Total Skills"
          value={stats?.total_skills || 0}
          icon="⚡"
          onClick={() => navigate('/skills')}
        />
        <StatCard
          title="Departments"
          value={stats?.total_departments || 0}
          icon="🏢"
        />
        <StatCard
          title="Avg Skills/Profile"
          value={stats?.avg_skills_per_profile || 0}
          icon="📊"
        />
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <h2>Top Skills</h2>
          {skillDistribution.length > 0 ? (
            <div className="chart-container">
              <div className="bar-chart">
                {skillDistribution.slice(0, 10).map((skill, idx) => (
                  <div key={idx} className="bar-item">
                    <div className="bar-label">{skill.name || skill.skill_name}</div>
                    <div className="bar-wrapper">
                      <div
                        className="bar-fill"
                        style={{
                          width: `${((skill.profile_count || skill.count) / (skillDistribution[0].profile_count || skillDistribution[0].count)) * 100}%`
                        }}
                      />
                      <span className="bar-value">{skill.profile_count || skill.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">No skill data available</p>
          )}
        </div>

        <div className="dashboard-card">
          <h2>Department Breakdown</h2>
          {departmentBreakdown.length > 0 ? (
            <div className="chart-container">
              <div className="pie-chart-legend">
                {departmentBreakdown.map((dept, idx) => (
                  <div key={idx} className="legend-item">
                    <span className="legend-color" style={{ background: getColor(idx) }} />
                    <span className="legend-label">{dept.department || 'Unassigned'}</span>
                    <span className="legend-value">{dept.count}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">No department data available</p>
          )}
        </div>

        <div className="dashboard-card">
          <h2>Proficiency Levels</h2>
          {stats?.proficiency_breakdown ? (
            <div className="chart-container">
              <div className="level-grid">
                {Object.entries(stats.proficiency_breakdown).map(([level, count]) => (
                  <div key={level} className="level-card">
                    <div className={`level-badge level-${level}`}>{level}</div>
                    <div className="level-count">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="no-data">No proficiency data available</p>
          )}
        </div>

        {isAdmin && (
          <div className="dashboard-card">
            <h2>Quick Actions</h2>
            <div className="quick-actions">
              <button onClick={() => navigate('/profiles/new')} className="action-btn">
                <span className="action-icon">➕</span>
                <span>Add New Profile</span>
              </button>
              <button onClick={() => navigate('/search')} className="action-btn">
                <span className="action-icon">🔍</span>
                <span>Search Profiles</span>
              </button>
              <button onClick={() => navigate('/profiles')} className="action-btn">
                <span className="action-icon">📋</span>
                <span>View All Profiles</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, onClick }) => (
  <div className={`stat-card ${onClick ? 'clickable' : ''}`} onClick={onClick}>
    <div className="stat-icon">{icon}</div>
    <div className="stat-content">
      <div className="stat-value">{value}</div>
      <div className="stat-title">{title}</div>
    </div>
  </div>
);

const getColor = (index) => {
  const colors = [
    '#4CAF50', '#2196F3', '#FF9800', '#9C27B0',
    '#F44336', '#00BCD4', '#FFEB3B', '#795548'
  ];
  return colors[index % colors.length];
};

export default Dashboard;
